/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import { onMounted, onBeforeUnmount } from 'vue'

export function useKeyboardNavigation (containerRef, orientation = 'horizontal') {
  function focusItem (items, index) {
    index = (index + items.length) % items.length
    items.forEach((el, i) => {
      el.setAttribute('tabindex', i === index ? '0' : '-1')
    })
    items[index]?.focus()
  }

  function getFocusableElements (el) {
    if (!el) return []
    // TODO: add other cases, for e.g. different roles (menubar, toolbar, tablist...) or elements (<ul>)
    // const role = el.getAttribute('role')
    if (el.tagName.toLowerCase() === 'nav') {
      return Array.from(el.querySelectorAll('ul > li > a:not(.disabled), ul > li > button:not([disabled])'))
    }
    return []
  }

  function handleVerticalKeydown (e, items) {
    const keys = ['ArrowUp', 'ArrowDown', 'Home', 'End']
    if (!keys.includes(e.key)) return

    e.preventDefault()
    const activeIndex = items.indexOf(document.activeElement)

    let nextIndex = activeIndex
    if (e.key === 'ArrowUp') nextIndex--
    else if (e.key === 'ArrowDown') nextIndex++
    else if (e.key === 'Home') nextIndex = 0
    else if (e.key === 'End') nextIndex = items.length - 1

    focusItem(items, nextIndex)
  }

  function handleHorizontalKeydown (e, items) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(e.key)) return

    e.preventDefault()
    const activeIndex = items.indexOf(document.activeElement)

    let nextIndex = activeIndex
    if (e.key === 'ArrowLeft') nextIndex--
    else if (e.key === 'ArrowRight') nextIndex++
    else if (e.key === 'Home') nextIndex = 0
    else if (e.key === 'End') nextIndex = items.length - 1

    focusItem(items, nextIndex)
  }

  function onKeydown (event) {
    if (event.key === 'Tab') return

    const el = event.currentTarget
    const items = getFocusableElements(el)

    if (orientation === 'vertical') handleVerticalKeydown(event, items)
    else handleHorizontalKeydown(event, items)
  }

  onMounted(() => {
    containerRef.value?.addEventListener('keydown', onKeydown)
  })

  onBeforeUnmount(() => {
    containerRef.value?.removeEventListener('keydown', onKeydown)
  })
}
